import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import Input from '../../components/Form/Input';
import AsyncSelect from '../../components/Form/AsyncSelect';
import TextArea from '../../components/Form/TextArea';
import Button from '../../components/Form/Button';
import { EmployeeDeduction } from '../../types';
import { employeesService } from '../../services';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';

interface EmployeeDeductionFormProps {
  deduction?: EmployeeDeduction;
  onSubmit: (data: Partial<EmployeeDeduction>) => void;
  onCancel: () => void;
}

const fetchEmployees = async (query: string, page: number) => {
  return employeesService.getAll({
    filters: query ? { name: { $containsi: query } } : undefined,
    pagination: { page, pageSize: 20 },
    sort: ['name:asc'],
  }) as any;
};

const EmployeeDeductionForm: React.FC<EmployeeDeductionFormProps> = ({ deduction, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<EmployeeDeduction>({
    defaultValues: deduction ? {
      ...deduction,
      date: formatDateForInput(deduction.date),
    } : {
      employeeId: '',
      amount: undefined as any,
      reason: '',
      date: new Date().toISOString().split('T')[0],
    },
  });

  const handleFormSubmit = (data: EmployeeDeduction) => {
    onSubmit({
      employeeId: data.employeeId,
      amount: Number(data.amount) || 0,
      reason: data.reason,
      date: formatDateForAPI(data.date),
      ...(deduction ? { id: deduction.id, documentId: deduction.documentId } : {}),
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {deduction ? 'Edit Employee Deduction' : 'Add Employee Deduction'}
      </h2>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Controller
            name="employeeId"
            control={control}
            rules={{ required: 'Employee is required' }}
            render={({ field }) => (
              <AsyncSelect
                label="Employee"
                name="employeeId"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.employeeId?.message as string}
                required
                placeholder="Select employee..."
                fetchOptions={fetchEmployees}
                getOptionLabel={(emp) => `${emp.name}${emp.code ? ` (${emp.code})` : ''}`}
                getOptionValue={(emp) => emp.documentId || emp.id?.toString()}
              />
            )}
          />

          <Input
            label="Amount"
            type="number"
            step="0.01"
            required
            {...register('amount', {
              required: 'Amount is required',
              min: { value: 0.01, message: 'Amount must be greater than 0' },
            })}
            error={errors.amount?.message}
            placeholder="0.00"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Date"
            type="date"
            required
            {...register('date', { required: 'Date is required' })}
            error={errors.date?.message}
          />
          <div></div>
        </div>

        <TextArea
          label="Reason"
          required
          {...register('reason', { required: 'Reason is required' })}
          error={errors.reason?.message}
          placeholder="Why is this amount being deducted from the employee?"
          rows={3}
        />

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {deduction ? 'Update Deduction' : 'Create Deduction'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeDeductionForm;
